// The kiosk form (UC-PATIENTS-023; kiosk-details.html, kiosk-medical-aid.html, kiosk.html): what the three steps hold, how an
// existing patient's record pre-fills it, the kiosk's own minimums (step 5 of the use case — the full patient rules apply at
// approval, BR-PATIENTS-024), and the builders that turn it into the contract's RegistrationSubmissionInput and ConsentInput.
// Every value is validated once more with the contract schema before it leaves the device, so a 422 from the server is the
// exception, not the way the form is checked.
import {
  CONSENT_PURPOSES, ConsentInputSchema, GUARDIAN_RELATIONSHIPS, RegistrationSubmissionInputSchema, REGISTRATION_FORM_VERSION, ageAt, initialsOf, normaliseMobile, parseSaId, saIdSchema,
  type ConsentInput, type ConsentPurpose, type ContactChannel, type Gender, type GuardianRelationship, type IdType, type PatientView, type PrincipalMemberIs, type RegistrationSubmissionInput, type SignerType,
} from "@visionx/contracts";
import type { Capture } from "./capture";
import type { KioskState } from "./mode";

export interface KioskDetails {
  title: string;
  initials: string;
  /** PAT-04: initials derive from the first names until the patient types their own. */
  initialsIsManual: boolean;
  firstNames: string;
  surname: string;
  idType: IdType;
  idNumber: string;
  idCountryOfIssue: string;
  dateOfBirth: string;
  gender: Gender | "";
  mobile: string;
  email: string;
  preferredChannel: ContactChannel;
  line1: string;
  suburb: string;
  city: string;
  postalCode: string;
  /** PAT-10, `health`: carried in the submission, stored at approval, never read back this sprint. */
  medicalHistory: string;
  idDocument: Capture | null;
  patientPhoto: Capture | null;
}

export interface KioskMedicalAid {
  hasMedicalAid: boolean;
  schemeId: string;
  /** Typed when the scheme is not in the list; resolved by staff at approval. */
  schemeName: string;
  planId: string;
  planName: string;
  optionId: string;
  optionName: string;
  membershipNumber: string;
  dependentCode: string;
  /** A dependant claiming through the account holder's scheme (BR-17). */
  sameAsMainAccount: boolean;
  principalMemberIs: PrincipalMemberIs;
  principalFirstNames: string;
  principalSurname: string;
  cardFront: Capture | null;
  cardBack: Capture | null;
}

export interface KioskConsent {
  granted: Record<ConsentPurpose, boolean>;
  textVersion: number | null;
  /** The name as signed — the patient's own, or the guardian's (BR-GOVERNANCE-020: the guardian's name travels as `signerName`). */
  signerName: string;
  signerType: SignerType;
  guardianIdNumber: string;
  guardianRelationship: GuardianRelationship | "";
  /** The guardian when they are a patient of the practice (the main account holder by default). */
  guardianPatientId: string | null;
  /** Standard base64 of the pad's PNG. */
  signaturePng: string | null;
  signedAt: string | null;
}

export interface KioskForm { details: KioskDetails; medicalAid: KioskMedicalAid; consent: KioskConsent }

export type Errors = Record<string, string>;

export { GUARDIAN_RELATIONSHIPS };
export const GUARDIAN_RELATIONSHIP_LABELS: Record<GuardianRelationship, string> = { Mother: "Mother", Father: "Father", LegalGuardian: "Legal guardian", Grandparent: "Grandparent", Other: "Other" };
export const CHANNEL_LABELS: Record<ContactChannel, string> = { WhatsApp: "WhatsApp", SMS: "SMS", Email: "Email", PhoneCall: "Phone call", None: "No electronic contact" };
/** The ID type choices as step 1 shows them; the blank-number message names "No ID number" by this label (UC-PATIENTS-023 A8). */
export const ID_TYPE_LABELS: Record<IdType, string> = { SaId: "South African ID", Passport: "Passport", None: "No ID number" };

const s = (v: string | null | undefined): string => v ?? "";

/** A fresh form, pre-filled from the handed-over patient's record when there is one (UC-PATIENTS-022 step 3). */
export function emptyForm(prefill: PatientView | null, defaults: { purposes: readonly { purpose: ConsentPurpose; preTicked: boolean }[]; textVersion: number | null }): KioskForm {
  const ma = prefill?.medicalAid ?? null;
  const granted = Object.fromEntries(CONSENT_PURPOSES.map((p) => [p, defaults.purposes.find((d) => d.purpose === p)?.preTicked ?? false])) as Record<ConsentPurpose, boolean>;
  granted.CareAndRecords = true;
  return {
    details: {
      title: s(prefill?.title), initials: s(prefill?.initials), initialsIsManual: !!prefill?.initials,
      firstNames: s(prefill?.firstNames), surname: s(prefill?.surname),
      idType: prefill?.idType ?? "SaId", idNumber: s(prefill?.idNumber), idCountryOfIssue: s(prefill?.idCountryOfIssue),
      dateOfBirth: s(prefill?.dateOfBirth), gender: prefill?.gender ?? "",
      mobile: s(prefill?.contact.mobile), email: s(prefill?.contact.email), preferredChannel: prefill?.contact.preferredChannel ?? "WhatsApp",
      line1: s(prefill?.address.line1), suburb: s(prefill?.address.suburb), city: s(prefill?.address.city), postalCode: s(prefill?.address.postalCode),
      medicalHistory: "", idDocument: null, patientPhoto: null,
    },
    medicalAid: {
      hasMedicalAid: prefill ? ma !== null : true,
      schemeId: s(ma?.schemeId), schemeName: ma?.schemeId ? "" : s(ma?.schemeName), planId: s(ma?.planId), planName: ma?.planId ? "" : s(ma?.planName), optionId: s(ma?.optionId), optionName: ma?.optionId ? "" : s(ma?.optionName),
      membershipNumber: s(ma?.membershipNumber), dependentCode: s(ma?.dependentCode), sameAsMainAccount: ma?.sameAsMainAccount ?? false,
      principalMemberIs: ma?.principalMemberIs ?? "Self", principalFirstNames: s(ma?.principalMember?.firstNames), principalSurname: s(ma?.principalMember?.surname),
      cardFront: null, cardBack: null,
    },
    consent: {
      granted, textVersion: defaults.textVersion,
      signerName: prefill ? `${prefill.firstNames} ${prefill.surname}` : "", signerType: "Self",
      guardianIdNumber: "", guardianRelationship: "", guardianPatientId: null, signaturePng: null, signedAt: null,
    },
  };
}

/** The date of birth the details give: entered, or derived from a valid SA ID (BR-06). */
export function dateOfBirthOf(d: KioskDetails, today: Date | string = new Date()): string | null {
  if (d.idType === "SaId") { const parsed = parseSaId(d.idNumber.trim(), today); if (parsed) return parsed.dateOfBirth; }
  return d.dateOfBirth || null;
}
/** PAT-73: under 18 at signing needs a guardian. Unknown (no date of birth yet) is treated as an adult. */
export function isMinorOf(d: KioskDetails, today: Date | string = new Date()): boolean {
  const dob = dateOfBirthOf(d, today);
  return dob !== null && ageAt(dob, today) < 18;
}

const looksLikeEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

/** An SA ID number the shared schema refuses, in the words the kiosk and the staff form both use (UC-PATIENTS-002 E1). */
export const saIdProblem = (schemaMessage: string | undefined): string =>
  schemaMessage === "must be 13 digits" ? "An ID number has 13 digits." : "This is not a valid South African ID number. Check the digits.";

export function validateDetails(d: KioskDetails): Errors {
  const e: Errors = {};
  if (!d.firstNames.trim()) e.firstNames = "Enter your first name.";
  if (!d.surname.trim()) e.surname = "Enter your surname.";
  if (d.idType === "SaId") {
    const r = saIdSchema.safeParse(d.idNumber);
    if (!d.idNumber.trim()) e.idNumber = `Enter your 13-digit South African ID number, or choose “${ID_TYPE_LABELS.None}” as the ID type.`;
    else if (!r.success) e.idNumber = saIdProblem(r.error.issues[0]?.message);
  } else if (d.idType === "Passport") {
    if (!d.idNumber.trim()) e.idNumber = "Enter your passport number.";
    if (!/^[A-Za-z]{2}$/.test(d.idCountryOfIssue.trim())) e.idCountryOfIssue = "Enter the two-letter country code of the passport (ZA, GB, …).";
  }
  if (d.idType !== "SaId" || e.idNumber) {
    if (!d.dateOfBirth) e.dateOfBirth = e.dateOfBirth ?? "Enter your date of birth.";
    if (!d.gender) e.gender = "Choose your gender.";
  }
  if (!d.mobile.trim() && !d.email.trim()) e.mobile = "Enter a mobile number or an email address so we can reach you.";
  if (d.mobile.trim() && normaliseMobile(d.mobile) === null) e.mobile = "This is not a phone number.";
  if (d.email.trim() && !looksLikeEmail(d.email)) e.email = "This is not an email address.";
  if ((d.preferredChannel === "WhatsApp" || d.preferredChannel === "SMS" || d.preferredChannel === "PhoneCall") && !d.mobile.trim()) e.preferredChannel = "That contact method needs a mobile number.";
  if (d.preferredChannel === "Email" && !d.email.trim()) e.preferredChannel = "Email contact needs an email address.";
  if (d.postalCode.trim() && !/^\d{4}$/.test(d.postalCode.trim())) e.postalCode = "A postal code has 4 digits.";
  return e;
}

export function validateMedicalAid(m: KioskMedicalAid): Errors {
  const e: Errors = {};
  if (!m.hasMedicalAid || m.sameAsMainAccount) return e;
  if (!m.schemeId && !m.schemeName.trim() && !m.membershipNumber.trim()) e.schemeId = "Choose your scheme or enter your member number — or press No medical aid.";
  if (m.principalMemberIs === "OtherPerson" && (!m.principalFirstNames.trim() || !m.principalSurname.trim())) e.principalMember = "Enter the principal member's first name and surname.";
  return e;
}

const SA_ID_OR_PASSPORT = /^[A-Za-z0-9]{6,20}$/;
export function validateConsent(c: KioskConsent, isMinor: boolean): Errors {
  const e: Errors = {};
  if (c.textVersion === null) e.textVersion = "No consent wording has been published yet. Please hand the device back to reception.";
  if (!c.granted.CareAndRecords) e.purposes = "Care and records is required.";
  if (isMinor && c.signerType !== "Guardian") e.signerType = "A parent or guardian must sign for a patient under 18.";
  if (c.signerType === "Guardian") {
    if (!c.signerName.trim()) e.guardianName = "Enter the parent or guardian's full name.";
    if (!c.guardianRelationship) e.guardianRelationship = "Choose the relationship.";
    const id = c.guardianIdNumber.trim();
    if (!id) e.guardianIdNumber = "Enter the parent or guardian's ID or passport number.";
    else if (/^\d{13}$/.test(id) ? !saIdSchema.safeParse(id).success : !SA_ID_OR_PASSPORT.test(id)) e.guardianIdNumber = "This is not a valid ID or passport number.";
  } else if (!c.signerName.trim()) e.signerName = "Enter your name as you sign it.";
  if (!c.signaturePng) e.signature = "Please sign in the box before you continue.";
  return e;
}

const opt = (v: string): string | undefined => (v.trim() === "" ? undefined : v.trim());
const captureOf = (c: Capture | null) => (c ? c.upload : undefined);

/** Step 1 as the contract carries it. */
export function buildDetailsStep(d: KioskDetails): Record<string, unknown> {
  const address = { line1: opt(d.line1), suburb: opt(d.suburb), city: opt(d.city), postalCode: opt(d.postalCode) };
  const hasAddress = Object.values(address).some((v) => v !== undefined);
  return {
    title: opt(d.title), initials: opt(d.initials) ?? opt(initialsOf(d.firstNames)), firstNames: d.firstNames.trim(), surname: d.surname.trim(),
    idType: d.idType, idNumber: d.idType === "None" ? undefined : opt(d.idNumber), idCountryOfIssue: d.idType === "Passport" ? opt(d.idCountryOfIssue)?.toUpperCase() : undefined,
    dateOfBirth: d.idType === "SaId" && parseSaId(d.idNumber.trim()) ? undefined : opt(d.dateOfBirth),
    gender: d.idType === "SaId" && parseSaId(d.idNumber.trim()) ? undefined : (d.gender || undefined),
    mobile: opt(d.mobile), email: opt(d.email.toLowerCase()), preferredChannel: d.preferredChannel,
    address: hasAddress ? address : undefined,
    medicalHistory: opt(d.medicalHistory),
    idDocument: captureOf(d.idDocument), patientPhoto: captureOf(d.patientPhoto),
  };
}

export function buildMedicalAidStep(m: KioskMedicalAid): Record<string, unknown> {
  if (!m.hasMedicalAid) return { hasMedicalAid: false };
  // A dependant on the account holder's scheme names none of their own: the pre-fill's scheme and number are the account
  // holder's, hidden on this step, and never sent back as the dependant's (BR-PATIENTS-027).
  const own = m.sameAsMainAccount ? {} : {
    schemeId: opt(m.schemeId), schemeName: m.schemeId ? undefined : opt(m.schemeName),
    planId: opt(m.planId), planName: m.planId ? undefined : opt(m.planName),
    optionId: opt(m.optionId), optionName: m.optionId ? undefined : opt(m.optionName),
    membershipNumber: opt(m.membershipNumber),
  };
  return {
    hasMedicalAid: true,
    ...own, dependentCode: opt(m.dependentCode),
    sameAsMainAccount: m.sameAsMainAccount || undefined,
    // On the account holder's scheme the principal member is the account holder (BR-23): whatever the hidden choice still held —
    // "Self" from the dependant's own membership before they unticked "different scheme" — was refused at approval (review,
    // 4 Oct 2026).
    principalMemberIs: m.sameAsMainAccount ? "MainAccount" : m.principalMemberIs,
    principalMember: !m.sameAsMainAccount && m.principalMemberIs === "OtherPerson" ? { firstNames: m.principalFirstNames.trim(), surname: m.principalSurname.trim() } : undefined,
    cardFront: captureOf(m.cardFront), cardBack: captureOf(m.cardBack),
  };
}

/** The consent answers as both the submission step and the standalone capture carry them (ConsentInputShape). */
export function buildConsentShape(c: KioskConsent): Record<string, unknown> {
  return {
    purposes: CONSENT_PURPOSES.map((purpose) => ({ purpose, granted: purpose === "CareAndRecords" ? true : !!c.granted[purpose] })),
    textVersion: c.textVersion ?? 0,
    signerName: opt(c.signerName),
    signerType: c.signerType,
    guardian: c.signerType === "Guardian" ? { relationship: c.guardianRelationship || undefined, idNumber: c.guardianIdNumber.trim(), patientId: c.guardianPatientId ?? undefined } : undefined,
    signatureImage: c.signaturePng ? { contentType: "image/png", data: c.signaturePng } : undefined,
  };
}

export type Built<T> = { ok: true; value: T } | { ok: false; issues: { path: string; message: string }[] };

const issuesOf = (error: { issues: { path: PropertyKey[]; message: string }[] }) => error.issues.map((i) => ({ path: i.path.map(String).join("."), message: i.message }));

/** POST /patients/{id}/consents for the handed-over patient: the kiosk's channel is InStoreTablet (BR-GOVERNANCE-023). */
export function buildConsentInput(c: KioskConsent): Built<ConsentInput> {
  const r = ConsentInputSchema.safeParse({ ...buildConsentShape(c), channel: "InStoreTablet" });
  return r.success ? { ok: true, value: r.data } : { ok: false, issues: issuesOf(r.error) };
}

/** POST /registration-submissions: NewPatient for a registration, UpdateExisting for a handed-over patient (UC-PATIENTS-023 step 4). */
export function buildSubmission(form: KioskForm, state: KioskState): Built<RegistrationSubmissionInput> {
  const body = {
    kind: state.patientId ? "UpdateExisting" : "NewPatient",
    existingPatientId: state.patientId ?? undefined,
    formVersion: REGISTRATION_FORM_VERSION,
    details: buildDetailsStep(form.details),
    medicalAid: buildMedicalAidStep(form.medicalAid),
    consent: buildConsentShape(form.consent),
  };
  const r = RegistrationSubmissionInputSchema.safeParse(body);
  return r.success ? { ok: true, value: r.data } : { ok: false, issues: issuesOf(r.error) };
}

/**
 * The error key a server or schema issue shows under (UC-PATIENTS-023 E1): the field's own name, except the signature image's
 * parts (`consent.signatureImage.data` too_many_pixels, too_large), which the pad shows, and the guardian block's, whose fields
 * are guardian-prefixed.
 */
export function errorKeyOf(path: string): string {
  const parts = path.split(".");
  if (parts.includes("signatureImage")) return "signature";
  const g = parts.indexOf("guardian");
  const field = g >= 0 ? parts[g + 1] : undefined;
  if (field) return `guardian${field.charAt(0).toUpperCase()}${field.slice(1)}`;
  return parts[parts.length - 1] ?? path;
}

/** Which step a server or schema issue belongs to, so the kiosk can go back to it (UC-PATIENTS-023 E1). */
export function stepOfIssue(path: string): 1 | 2 | 3 {
  if (path.startsWith("consent") || path.startsWith("purposes") || path.startsWith("signature") || path.startsWith("signer") || path.startsWith("guardian") || path.startsWith("textVersion")) return 3;
  if (path.startsWith("medicalAid") || path.startsWith("scheme") || path.startsWith("membership") || path.startsWith("card")) return 2;
  return 1;
}
